import { useState, useCallback, useLayoutEffect } from 'react';

/**
 * Viewport-safe placement for a fixed-position floating panel anchored to a
 * trigger (`below`: under the anchor; `above`: panel sits above, e.g. FAB).
 * Aligns the panel's right edge to the anchor's right edge, then clamps
 * horizontally so the panel never spills past the viewport.
 *
 * Subscribes to resize, scroll (capture), and ResizeObserver on the anchor so
 * the panel stays aligned while the page moves.
 *
 * @param {Object} options
 * @param {boolean} options.open - When false, placement is cleared and listeners are removed.
 * @param {React.RefObject<HTMLElement|null>} options.anchorRef - Ref on the trigger wrapper (or trigger).
 * @param {number} [options.maxWidth=320] - Desired panel width (px); capped by viewport.
 * @param {number} [options.minWidth=200] - Minimum width after viewport cap.
 * @param {number} [options.edgePadding=12] - Minimum gap from viewport left/right/bottom.
 * @param {number} [options.gap=8] - Gap between anchor and panel (`below`: under anchor; `above`: above anchor).
 * @param {number} [options.maxHeightCap=384] - Max panel height (px), e.g. 24rem.
 * @param {number} [options.minVisibleHeight=160] - Floor for maxHeight when space is tight.
 * @param {boolean} [options.autoHeight=false] - When true, omit maxHeight so the panel grows with its content.
 * @param {'below'|'above'} [options.placement='below'] - `above` uses fixed `bottom` (e.g. FAB menus).
 * @returns {{ panelRect: { left: number, width: number, maxHeight?: number, top?: number, bottom?: number } | null }}
 *
 * @example
 * const anchorRef = useRef(null);
 * const { panelRect } = useViewportAnchoredPanel({
 *   open: isOpen,
 *   anchorRef,
 *   maxWidth: 320,
 * });
 * return (
 *   <div ref={anchorRef} className="relative shrink-0">
 *     <button onClick={toggle}>Filter</button>
 *     {isOpen && (
 *       <div
 *         className="fixed z-20 …"
 *         style={
 *           panelRect
 *             ? { left: panelRect.left, top: panelRect.top, bottom: panelRect.bottom, width: panelRect.width, maxHeight: panelRect.maxHeight }
 *             : { visibility: 'hidden', pointerEvents: 'none' }
 *         }
 *       >
 *         …
 *       </div>
 *     )}
 *   </div>
 * );
 */
/** Shared placement options for header user-profile dropdowns. */
export const USER_MENU_PANEL_OPTIONS = {
  maxWidth: 288,
  minWidth: 240,
  autoHeight: true,
};

/**
 * Inline styles for a fixed panel positioned by {@link useViewportAnchoredPanel}.
 *
 * @param {{ left: number, width: number, maxHeight?: number, top?: number, bottom?: number } | null} panelRect
 * @returns {React.CSSProperties}
 */
export function getViewportPanelStyle(panelRect) {
  if (!panelRect) {
    return { visibility: 'hidden', pointerEvents: 'none' };
  }
  const style = {
    left: panelRect.left,
    width: panelRect.width,
  };
  if (panelRect.top != null) style.top = panelRect.top;
  if (panelRect.bottom != null) style.bottom = panelRect.bottom;
  if (panelRect.maxHeight != null) style.maxHeight = panelRect.maxHeight;
  return style;
}

export function useViewportAnchoredPanel({
  open,
  anchorRef,
  maxWidth = 320,
  minWidth = 200,
  edgePadding = 12,
  gap = 8,
  maxHeightCap = 384,
  minVisibleHeight = 160,
  autoHeight = false,
  placement = 'below',
}) {
  const [panelRect, setPanelRect] = useState(null);

  const updatePlacement = useCallback(() => {
    const el = anchorRef?.current;
    if (!el || !open) {
      setPanelRect(null);
      return;
    }
    const anchor = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(maxWidth, Math.max(minWidth, vw - 2 * edgePadding));
    let left = anchor.right - width;
    left = Math.max(edgePadding, Math.min(left, vw - edgePadding - width));

    if (placement === 'above') {
      const bottom = vh - anchor.top + gap;
      const rect = { left, width, bottom };
      if (!autoHeight) {
        const rawAbove = anchor.top - gap - edgePadding;
        rect.maxHeight = Math.min(maxHeightCap, Math.max(80, rawAbove));
      }
      setPanelRect(rect);
      return;
    }

    const top = anchor.bottom + gap;
    const rect = { left, top, width };
    if (!autoHeight) {
      rect.maxHeight = Math.max(
        minVisibleHeight,
        Math.min(maxHeightCap, vh - top - edgePadding)
      );
    }
    setPanelRect(rect);
  }, [
    open,
    anchorRef,
    maxWidth,
    minWidth,
    edgePadding,
    gap,
    maxHeightCap,
    minVisibleHeight,
    autoHeight,
    placement,
  ]);

  useLayoutEffect(() => {
    updatePlacement();
    if (!open) return undefined;

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => updatePlacement())
        : null;
    if (ro && anchorRef.current) {
      ro.observe(anchorRef.current);
    }
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [open, updatePlacement, anchorRef]);

  return { panelRect };
}

export default useViewportAnchoredPanel;
